import React from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
} from '@tanstack/react-table';

export interface ResidentFinancialHistory {
  id: string;
  invoiceId: string;
  amount: string;
  date: string;
  status: 'PAID' | 'OVERDUE' | 'PARTIAL' | 'PENDING' | 'CANCELLED';
  downloadUrl: string;
}

interface ResidentFinancialHistoryTableProps {
  data: ResidentFinancialHistory[];
  isLoading?: boolean;
  className?: string;
}

export const ResidentFinancialHistoryTable: React.FC<
  ResidentFinancialHistoryTableProps
> = ({ data, isLoading, className = '' }) => {
  const columns: ColumnDef<ResidentFinancialHistory>[] = [
    {
      accessorKey: 'invoiceId',
      header: () => (
        <div className="text-center text-text-primary font-bold">
          Invoice ID
        </div>
      ),
      cell: (info) => (
        <div className="text-text-primary">{String(info.getValue())}</div>
      ),
    },
    {
      accessorKey: 'amount',
      header: () => (
        <div className="text-center text-text-primary font-bold">Amount</div>
      ),
      cell: (info) => (
        <div className="text-center text-text-primary">
          {String(info.getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'date',
      header: () => (
        <div className="text-center text-text-primary font-bold">Date</div>
      ),
      cell: (info) => (
        <div className="text-center text-text-primary">
          {String(info.getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: () => (
        <div className="text-center text-text-primary font-bold">Status</div>
      ),
      cell: (info) => {
        const value = String(
          info.getValue(),
        ) as ResidentFinancialHistory['status'];

        let color = 'bg-gray-100 text-gray-700';
        switch (value) {
          case 'PAID':
            color = 'bg-green-100 text-green-700';
            break;
          case 'PARTIAL':
            color = 'bg-yellow-100 text-yellow-700';
            break;
          case 'OVERDUE':
            color = 'bg-red-100 text-red-700';
            break;
          case 'PENDING':
            color = 'bg-blue-100 text-blue-700';
            break;
          case 'CANCELLED':
            color = 'bg-gray-300 text-gray-700';
            break;
          default:
            color = 'bg-gray-100 text-gray-700';
        }

        return (
          <div className="text-center">
            <span
              className={`px-3 py-1 rounded-full text-xs font-bold ${color}`}
            >
              {value}
            </span>
          </div>
        );
      },
    },
    {
      id: 'actions',
      header: () => (
        <div className="text-center text-text-primary font-bold">Actions</div>
      ),
      cell: ({ row }) => (
        <div className="text-center text-text-primary">
          <a
            href={row.original.downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline font-medium"
          >
            Download
          </a>
        </div>
      ),
    },
  ];

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading) {
    return (
      <div className="p-4 space-y-2">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-10 bg-gray-200 animate-pulse rounded" />
        ))}
      </div>
    );
  }

  return (
    <div
      className={`min-w-3xl ${className}`}
      style={{ scrollbarWidth: 'none' }}
    >
      <table className="w-full border-collapse">
        <thead className="bg-gray-100">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className="p-1 md:p-3 text-sm md:text-base font-semibold text-gray-700 border text-center"
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="border-b hover:bg-gray-50">
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="p-1 md:p-3 text-sm md:text-base text-center">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={columns.length}
                className="p-4 text-gray-400 text-center"
              >
                No records found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};
